import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Chip, Grid, Alert, CircularProgress,
  Avatar, TextField, MenuItem, Table, TableHead, TableRow,
  TableCell, TableBody, TableContainer, LinearProgress,
  Tooltip, IconButton, Tab, Tabs,
} from '@mui/material';
import { BeachAccess, Warning, Refresh, TrendingDown } from '@mui/icons-material';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';
const YEAR = new Date().getFullYear();

function apiFetch(path: string) {
  return fetch(`${API}${path}`, {
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

const DOW_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function MiniBar({ value, max, color }: { value: number; max: number; color: string }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ flex: 1, height: 10, bgcolor: '#f5f5f5', borderRadius: 5, overflow: 'hidden' }}>
        <Box sx={{ height: '100%', width: `${pct}%`, bgcolor: color, borderRadius: 5 }} />
      </Box>
      <Typography variant="caption" sx={{ minWidth: 32, textAlign: 'right' }}>{value}</Typography>
    </Box>
  );
}

export default function LeavePatterns() {
  const [year, setYear]     = useState(YEAR);
  const [dept, setDept]     = useState('');
  const [depts, setDepts]   = useState<any[]>([]);
  const [data, setData]     = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab]       = useState(0);
  const [error, setError]   = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [d, deptsData] = await Promise.all([
        apiFetch(`/api/leave-patterns?year=${year}${dept ? `&deptId=${dept}` : ''}`),
        depts.length === 0 ? apiFetch('/api/leave-patterns/depts') : Promise.resolve(depts),
      ]);
      setData(d);
      if (depts.length === 0) setDepts(deptsData);
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }, [year, dept]);

  useEffect(() => { load(); }, [load]);

  const dow     = data?.dowHeatmap ?? [];
  const monthly = data?.monthlyTrend ?? [];
  const lead    = data?.leadTime ?? [];
  const holiday = data?.holidayAdjacent ?? [];
  const burnout = data?.burnoutRisk ?? [];
  const topLeave = data?.topLeave ?? [];
  const deptComp = data?.deptComparison ?? [];
  const typeBreak = data?.leaveTypeBreakdown ?? [];
  const longRuns  = data?.longLeaveRuns ?? [];
  const declined  = data?.declinedReasons ?? [];

  const maxDow     = Math.max(...dow.map((d: any) => d.requests), 1);
  const maxMonthly = Math.max(...monthly.map((m: any) => m.days), 1);
  const maxDeptDays = Math.max(...deptComp.map((d: any) => parseFloat(d.approvedDays || 0)), 1);

  const totalApprovedDays = monthly.reduce((s: number, m: any) => s + parseFloat(m.days || 0), 0);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: '#1565c0', width: 48, height: 48 }}><BeachAccess /></Avatar>
          <Box>
            <Typography variant="h5" fontWeight={700}>Leave Pattern Analysis</Typography>
            <Typography variant="body2" color="text.secondary">
              Trends, timing, burnout risk, and department comparisons
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField select size="small" label="Department" value={dept}
            onChange={e => setDept(e.target.value)} sx={{ minWidth: 160 }}>
            <MenuItem value="">All Departments</MenuItem>
            {depts.map((d: any) => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Year" value={year}
            onChange={e => setYear(parseInt(e.target.value))} sx={{ width: 90 }}>
            {[YEAR - 1, YEAR, YEAR + 1].map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
          </TextField>
          <Tooltip title="Refresh"><IconButton onClick={load}><Refresh /></IconButton></Tooltip>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {burnout.length > 0 && (
        <Alert severity="warning" icon={<Warning />} sx={{ mb: 2 }}>
          <strong>{burnout.length} employee{burnout.length > 1 ? 's have' : ' has'} used less than 30% of their leave</strong> — potential burnout risk.
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
      ) : (
        <>
          {/* Summary chips */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <Chip label={`${Math.round(totalApprovedDays)} approved leave days`} color="primary" variant="outlined" />
            {burnout.length > 0 && <Chip icon={<Warning />} label={`${burnout.length} at burnout risk`} sx={{ bgcolor: '#fff3e0', color: '#e65100' }} />}
            {holiday.length > 0 && <Chip label={`${holiday.reduce((s: number, h: any) => s + h.requests, 0)} holiday-adjacent requests`} sx={{ bgcolor: '#e8f5e9', color: '#2e7d32' }} />}
            {longRuns.length > 0 && <Chip label={`${longRuns.length} long runs (≥5 days)`} sx={{ bgcolor: '#f3e5f5', color: '#6a1b9a' }} />}
          </Box>

          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, '& .MuiTab-root': { textTransform: 'none' } }}>
            <Tab label="Timing & Trends" />
            <Tab label="Departments" />
            <Tab label={`Burnout Risk (${burnout.length})`} />
            <Tab label="Other Insights" />
          </Tabs>

          {tab === 0 && (
            <Grid container spacing={2}>
              {/* Day of week heatmap */}
              <Grid size={{ xs: 12, md: 5 }}>
                <Paper sx={{ p: 2, height: '100%' }}>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>📅 Leave by Day of Week</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                    Approved requests by day leave starts
                  </Typography>
                  {DOW_LABELS.map((label, i) => {
                    const row = dow.find((d: any) => d.dow === i);
                    const req  = row?.requests ?? 0;
                    const isFriMon = i === 1 || i === 5;
                    return (
                      <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.75 }}>
                        <Typography variant="body2" sx={{ minWidth: 32, fontWeight: isFriMon ? 700 : 400,
                          color: isFriMon ? '#C08A2E' : 'inherit' }}>{label}</Typography>
                        <Box sx={{ flex: 1 }}>
                          <MiniBar value={req} max={maxDow}
                            color={isFriMon ? '#C08A2E' : i === 0 || i === 6 ? '#bdbdbd' : '#1565c0'} />
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {row ? `${parseFloat(row.days).toFixed(1)} days` : '—'}
                        </Typography>
                      </Box>
                    );
                  })}
                  <Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: 'block' }}>
                    Monday & Friday highlighted in orange
                  </Typography>
                </Paper>
              </Grid>

              {/* Monthly trend */}
              <Grid size={{ xs: 12, md: 7 }}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>📈 Monthly Leave Days ({year})</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-end', height: 120, mb: 0.5 }}>
                    {monthly.map((m: any) => {
                      const h = maxMonthly > 0 ? Math.max(4, (parseFloat(m.days) / maxMonthly) * 110) : 4;
                      return (
                        <Tooltip key={m.month} title={`${m.monthLabel}: ${parseFloat(m.days).toFixed(1)} days (${m.requests} requests)`}>
                          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                            <Box sx={{ width: '70%', height: h, bgcolor: '#1565c0', borderRadius: '3px 3px 0 0', opacity: 0.8, transition: 'height 0.3s' }} />
                            <Typography variant="caption" sx={{ fontSize: '0.55rem', color: 'text.secondary' }}>{m.monthLabel}</Typography>
                          </Box>
                        </Tooltip>
                      );
                    })}
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                    {monthly.filter((m: any) => parseFloat(m.days) > 0).sort((a: any, b: any) => b.days - a.days).slice(0, 3).map((m: any) => (
                      <Chip key={m.month} size="small" label={`${m.monthLabel}: ${parseFloat(m.days).toFixed(0)}d peak`}
                        sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontSize: '0.65rem' }} />
                    ))}
                  </Box>
                </Paper>
              </Grid>

              {/* Lead time */}
              <Grid size={{ xs: 12, md: 5 }}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>⏱️ Request Lead Time</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    How far in advance leave is requested
                  </Typography>
                  {lead.map((l: any) => {
                    const maxReq = Math.max(...lead.map((x: any) => x.requests), 1);
                    return (
                      <Box key={l.bucket} sx={{ mb: 0.75 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                          <Typography variant="body2">{l.bucket}</Typography>
                          <Typography variant="caption" color="text.secondary">{l.requests} requests</Typography>
                        </Box>
                        <LinearProgress variant="determinate" value={Math.round((l.requests / maxReq) * 100)}
                          sx={{ height: 8, borderRadius: 4, '& .MuiLinearProgress-bar': { bgcolor: '#6a1b9a' } }} />
                      </Box>
                    );
                  })}
                </Paper>
              </Grid>

              {/* Holiday adjacent */}
              <Grid size={{ xs: 12, md: 7 }}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>🎉 Leave Around Public Holidays</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Requests starting within 2 days of a public holiday
                  </Typography>
                  {holiday.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No holiday-adjacent leave found.</Typography>
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Holiday</TableCell>
                            <TableCell align="right">Requests</TableCell>
                            <TableCell align="right">Days</TableCell>
                            <TableCell align="right">Employees</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {holiday.map((h: any) => (
                            <TableRow key={`${h.holidayName}-${h.holidayDate}`} hover>
                              <TableCell>
                                <Typography variant="body2" fontWeight={600}>{h.holidayName}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {h.holidayDate?.toString().slice(0, 10)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">{h.requests}</TableCell>
                              <TableCell align="right">{parseFloat(h.days).toFixed(1)}</TableCell>
                              <TableCell align="right">{h.employees}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Paper>
              </Grid>

              {/* Leave type breakdown */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>🗂️ Leave by Type</Typography>
                  {typeBreak.map((t: any) => (
                    <Box key={t.leaveType} sx={{ mb: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2">{t.leaveType}</Typography>
                          <Chip size="small" label={t.isPaid ? 'Paid' : 'Unpaid'}
                            sx={{ fontSize: '0.55rem', height: 16,
                              bgcolor: t.isPaid ? '#e8f5e9' : '#fce4ec',
                              color: t.isPaid ? '#2e7d32' : '#c62828' }} />
                        </Box>
                        <Typography variant="caption" fontWeight={700} color="#1565c0">{t.pct}%</Typography>
                      </Box>
                      <LinearProgress variant="determinate" value={parseFloat(t.pct)}
                        sx={{ height: 6, borderRadius: 3, '& .MuiLinearProgress-bar': { bgcolor: '#1565c0' } }} />
                      <Typography variant="caption" color="text.secondary">{parseFloat(t.days).toFixed(1)} days · {t.requests} requests</Typography>
                    </Box>
                  ))}
                </Paper>
              </Grid>

              {/* Top leave takers */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>🏆 Top Leave Takers</Typography>
                  {topLeave.slice(0, 8).map((e: any, i: number) => (
                    <Box key={e.employeeId} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                      <Avatar sx={{ width: 24, height: 24, fontSize: '0.65rem', bgcolor: i < 3 ? '#C08A2E' : '#e0e0e0', color: i < 3 ? '#fff' : '#546e7a' }}>
                        {i + 1}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>{e.fullName}</Typography>
                        <Typography variant="caption" color="text.secondary">{e.department}</Typography>
                      </Box>
                      <Chip size="small" label={`${parseFloat(e.totalDays).toFixed(1)}d`}
                        sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 700 }} />
                    </Box>
                  ))}
                </Paper>
              </Grid>
            </Grid>
          )}

          {tab === 1 && (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Department</TableCell>
                    <TableCell align="right">Headcount</TableCell>
                    <TableCell align="right">Approved Days</TableCell>
                    <TableCell align="right">Avg / Employee</TableCell>
                    <TableCell align="right">Requests</TableCell>
                    <TableCell align="right">Declined</TableCell>
                    <TableCell>Volume</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deptComp.map((d: any) => (
                    <TableRow key={d.department} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{d.department}</TableCell>
                      <TableCell align="right">{d.headcount}</TableCell>
                      <TableCell align="right">{parseFloat(d.approvedDays || 0).toFixed(1)}</TableCell>
                      <TableCell align="right">
                        <Chip size="small" label={`${d.avgDaysPerEmployee ?? 0}d`}
                          sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontSize: '0.65rem' }} />
                      </TableCell>
                      <TableCell align="right">{d.approvedRequests}</TableCell>
                      <TableCell align="right">
                        {d.declinedRequests > 0 && (
                          <Chip size="small" label={d.declinedRequests}
                            sx={{ bgcolor: '#ffebee', color: '#b71c1c', fontSize: '0.65rem' }} />
                        )}
                        {d.declinedRequests === 0 && '—'}
                      </TableCell>
                      <TableCell sx={{ minWidth: 100 }}>
                        <MiniBar value={parseFloat(d.approvedDays || 0)} max={maxDeptDays} color="#1565c0" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {tab === 2 && (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <Alert severity="warning" icon={<TrendingDown />}>
                  These employees have used less than <strong>30%</strong> of their entitled leave this year — they may be at risk of burnout. Consider a check-in conversation.
                </Alert>
              </Grid>
              {burnout.length === 0 ? (
                <Grid size={{ xs: 12 }}>
                  <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary">No burnout risk employees found 🎉</Typography>
                  </Paper>
                </Grid>
              ) : (
                <Grid size={{ xs: 12 }}>
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>Employee</TableCell>
                          <TableCell>Department</TableCell>
                          <TableCell>Position</TableCell>
                          <TableCell align="right">Entitled (days)</TableCell>
                          <TableCell align="right">Used (days)</TableCell>
                          <TableCell>Utilisation</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {burnout.map((e: any) => {
                          const pct = parseFloat(e.utilisationPct || 0);
                          return (
                            <TableRow key={e.employeeId} hover
                              sx={{ bgcolor: pct < 10 ? '#ffebee22' : undefined }}>
                              <TableCell sx={{ fontWeight: 600 }}>{e.fullName}</TableCell>
                              <TableCell>{e.department}</TableCell>
                              <TableCell><Typography variant="caption">{e.position}</Typography></TableCell>
                              <TableCell align="right">{parseFloat(e.entitled).toFixed(1)}</TableCell>
                              <TableCell align="right">{parseFloat(e.used).toFixed(1)}</TableCell>
                              <TableCell sx={{ minWidth: 140 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <LinearProgress variant="determinate" value={pct}
                                    sx={{ flex: 1, height: 8, borderRadius: 4,
                                      '& .MuiLinearProgress-bar': { bgcolor: pct < 10 ? '#b71c1c' : '#e65100' } }} />
                                  <Chip size="small" label={`${pct}%`}
                                    sx={{ bgcolor: pct < 10 ? '#ffebee' : '#fff3e0',
                                      color: pct < 10 ? '#b71c1c' : '#e65100', fontWeight: 700, fontSize: '0.65rem' }} />
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              )}
            </Grid>
          )}

          {tab === 3 && (
            <Grid container spacing={2}>
              {/* Long leave runs */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>📆 Long Leave Runs (≥5 days)</Typography>
                  {longRuns.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No long leave runs found.</Typography>
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Employee</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell align="right">Days</TableCell>
                            <TableCell>Dates</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {longRuns.map((r: any, i: number) => (
                            <TableRow key={i} hover>
                              <TableCell>
                                <Typography variant="body2" fontWeight={600}>{r.fullName}</Typography>
                                <Typography variant="caption" color="text.secondary">{r.department}</Typography>
                              </TableCell>
                              <TableCell><Chip size="small" label={r.leaveType} sx={{ fontSize: '0.6rem' }} /></TableCell>
                              <TableCell align="right">
                                <Chip size="small" label={`${parseFloat(r.days).toFixed(0)}d`}
                                  sx={{ bgcolor: '#f3e5f5', color: '#6a1b9a', fontWeight: 700 }} />
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption" color="text.secondary">
                                  {r.startDate?.toString().slice(0, 10)} →<br/>{r.endDate?.toString().slice(0, 10)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Paper>
              </Grid>

              {/* Declined reasons */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>❌ Declined Leave — Reasons</Typography>
                  {declined.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No declined requests found.</Typography>
                  ) : (
                    declined.map((r: any) => (
                      <Box key={r.reason} sx={{ mb: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: '70%' }}>{r.reason}</Typography>
                          <Typography variant="caption" fontWeight={700} color="#b71c1c">{r.pct}%</Typography>
                        </Box>
                        <LinearProgress variant="determinate" value={parseFloat(r.pct)}
                          sx={{ height: 6, borderRadius: 3, '& .MuiLinearProgress-bar': { bgcolor: '#b71c1c' } }} />
                        <Typography variant="caption" color="text.secondary">{r.count} request{r.count > 1 ? 's' : ''}</Typography>
                      </Box>
                    ))
                  )}
                </Paper>
              </Grid>
            </Grid>
          )}
        </>
      )}
    </Box>
  );
}
